import React, { useEffect, useRef, useState } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
} from 'mdbreact';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { getData } from '../../services/auth';
import NavBar from '../../components/NavBar';
import Loading from '../../components/Loading';
import TabelaGerencias from './TabelaGerencias';
import TabelaLotacoes from './TabelaLotacoes';

// Gerência × Lotação (#174) — substitui a tela antiga, que só listava e criava
// em lote as alas Alfa..Juliet.
//
// Lotação é a tripla (gerência, escala, ala) e o par é único no banco. Não há
// exclusão: as FKs são ON DELETE SET NULL contra colunas NOT NULL, então apagar
// lotação com histórico é recusado pelo Postgres. Tirar de circulação é
// inativar, e só quando ninguém ativo estiver lotado ali.
//
// A listagem mostra ATIVAS E INATIVAS de propósito: o select de ala do modal
// omite os pares que a gerência+escala já tem (o par continua ocupado mesmo
// inativo), então esconder as inativas deixaria "Reativar" sem onde clicar.

// mesmos componentes de pages/Frota/Contratos/index.js:42-52. Se um terceiro
// lugar precisar, extrair para components/ em vez de duplicar de novo.
const Obrigatorio = ({ texto }) => (
  <>
    {texto} <span className="red-text">*</span>
  </>
);

const LegendaObrigatorios = () => (
  <p className="grey-text mt-3 mb-0" style={{ fontSize: 12 }}>
    <span className="red-text">*</span> campos obrigatórios
  </p>
);

// A paleta vive em styles/cores-sobrias.css (#184). Referenciar a variável, e
// não copiar o hex, evita a paleta mista que a auditoria de 05/08 encontrou.
//
// Só a destrutiva sobrevive aqui: a construtiva (verde de "Reativar") só era
// usada no botão da lista de lotações, que virou TabelaLotacoes.js (#205) e
// declara a própria cópia — mesmo padrão de TabelaGerencias.js.
const COR_DESTRUTIVA = 'var(--sig-destrutiva)';

// comparação de nome de ala sem acento e sem caixa, para não cadastrar
// "Motoristas" ao lado de "motoristas"
const chaveNome = texto =>
  String(texto || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // marcas de acento separadas pelo NFD
    .toLowerCase();

// Compara duas estruturas formais (ex.: "3.1.2" e "3.1.10") segmento a
// segmento, convertendo cada um para número. Existe porque a ordem que
// importa pra tela é a hierárquica, e um `localeCompare` simples é
// exatamente o que produz a ordem errada: comparando como texto, "3.1.10"
// vem antes de "3.1.2" porque o caractere '1' é menor que '2'.
//
// GerenciaController.index ordena por `estrutura_logica`, e quatro
// gerências dividem a lógica 3.1 (OpFoco, Volante 3.1.6, CICC 3.1.7, OpInt
// 3.1.8) — saem embaralhadas entre si a cada carga, e a mãe aparece no meio
// da lista. Reordenar aqui em vez de mudar o `order` do backend: a rota
// /gerencia alimenta outros 12 consumidores no SEGOV, e trocar o contrato
// dela por causa desta tela é risco desnecessário.
const compararEstruturaFormal = (formalA, formalB) => {
  const segmentosA = String(formalA || '').split('.');
  const segmentosB = String(formalB || '').split('.');
  const tamanho = Math.max(segmentosA.length, segmentosB.length);

  for (let i = 0; i < tamanho; i += 1) {
    // um dos dois acabou os segmentos: é prefixo do outro (3.1 é pai de
    // 3.1.1), então a mais curta — a mãe — vem primeiro
    if (segmentosA[i] === undefined) return -1;
    if (segmentosB[i] === undefined) return 1;

    const numA = Number(segmentosA[i]);
    const numB = Number(segmentosB[i]);
    // segmento não numérico não pode quebrar a comparação: cai para texto
    // só nesse segmento, em vez de virar NaN e empatar tudo
    if (Number.isNaN(numA) || Number.isNaN(numB)) {
      const porTexto = segmentosA[i].localeCompare(segmentosB[i]);
      if (porTexto !== 0) return porTexto;
    } else if (numA !== numB) {
      return numA - numB;
    }
  }
  return 0;
};

export default function Lotacao() {
  const [gerencias, setGerencias] = useState([]);
  const [gerencia, setGerencia] = useState(null);

  const [lotacoes, setLotacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erroLista, setErroLista] = useState('');
  // Espelha erroLista, mas para a listagem de gerências. GET não passa pelo
  // toast do interceptor (api.js:70), e sem isto uma falha de rede deixava
  // `gerencias` vazio e a tela mostrava "Nenhuma gerência no seu escopo." —
  // que o usuário lê como perda de permissão, não como falha de carregamento
  // (fix final, #205).
  const [erroListaGerencias, setErroListaGerencias] = useState('');
  // id da lotação -> quantas pessoas barraram a inativação. Fica fixo na linha:
  // o toast do interceptor some antes de a pessoa ler a instrução.
  const [bloqueios, setBloqueios] = useState({});
  // id da gerência -> quantas pessoas (na gerência ou abaixo dela) barraram a
  // inativação. Mesmo raciocínio de `bloqueios`, mas para a tabela de gerências.
  const [bloqueiosGerencia, setBloqueiosGerencia] = useState({});

  const [modalAberto, setModalAberto] = useState(false);
  const [escalas, setEscalas] = useState([]);
  const [alas, setAlas] = useState([]);
  const [escalaId, setEscalaId] = useState('');
  const [alaId, setAlaId] = useState('');
  const [modoNovaAla, setModoNovaAla] = useState(false);
  const [novaAla, setNovaAla] = useState('');
  const [salvando, setSalvando] = useState(false);

  // ---- modal de nova gerência -------------------------------------------
  // A tela antiga de Gerências (/gerencias) foi descontinuada; a tela de
  // Lotações passou a listar e ativar/desativar gerências. Cadastrar aqui
  // evita pular de tela no meio do fluxo: quem cria a gerência é quem vai
  // vincular as lotações dela em seguida.
  const [modalGerencia, setModalGerencia] = useState(false);
  const [novaGerencia, setNovaGerencia] = useState({ nome: '', sigla: '' });
  // gerência acima na hierarquia e o que a nova enxerga:
  // 'propria' = só ela e as subordinadas | 'herdar' = o mesmo que o pai vê
  const [gerenciaPai, setGerenciaPai] = useState(null);
  const [visibilidade, setVisibilidade] = useState('propria');

  const carregarGerencias = async () => {
    setErroListaGerencias('');
    try {
      const { data } = await api.get('/gerencia', {
        params: { com_contagem: true },
      });
      // Ordenada por `estrutura_formal` (compararEstruturaFormal, acima) e
      // não pelo `order` que a API já devolve (por `estrutura_logica`): com
      // quatro gerências dividindo a lógica 3.1, a ordem do backend embaralha
      // essas quatro entre si a cada carga e deixa a mãe no meio da lista.
      const opcoes = data
        .map(g => ({
          value: g.id,
          label: g.nome,
          sigla: g.sigla,
          logica: g.estrutura_logica,
          formal: g.estrutura_formal,
          ativo: g.ativo,
          pessoas: g.pessoas_ativas,
        }))
        .sort((a, b) => compararEstruturaFormal(a.formal, b.formal));
      setGerencias(opcoes);

      // pré-seleciona a gerência do próprio usuário. gerencia_id vem direto
      // do payload do JWT (SessionController: usuario.lotacao.gerencia_id) e
      // casa exato, o que `logica` sozinho não fazia — quatro gerências
      // dividem a mesma estrutura lógica 3.1 (OpFoco, Volante, CICC, OpInt),
      // então comparar só por ela pegava a primeira do array, uma gerência
      // arbitrária entre as quatro.
      //
      // Fallback por `logica`: cobre token antigo, emitido antes de
      // gerencia_id existir no payload, que ainda não tem o campo.
      const minha =
        opcoes.find(o => o.value === getData().gerencia_id) ||
        opcoes.find(o => o.logica === getData().gerencia);

      // Só quando ainda NÃO há seleção: esta função também roda depois de
      // criar uma gerência, e resetar a escolha ali colocava duas cargas de
      // lotação em voo ao mesmo tempo — a lista terminava mostrando a
      // gerência errada.
      if (minha) setGerencia(atual => atual || minha);
    } catch (e) {
      // GET não passa pelo toast do interceptor — a tela avisa (mesmo padrão
      // de carregarLotacoes, logo abaixo)
      setErroListaGerencias('Não foi possível carregar as gerências.');
      setGerencias([]);
    }
  };

  // Guarda qual gerência é a última pedida. Sem isso, trocar de gerência antes
  // de a resposta anterior chegar deixa a lista da gerência errada na tela.
  const ultimoPedido = useRef(null);

  const carregarLotacoes = async gerenciaId => {
    ultimoPedido.current = gerenciaId;
    setLoading(true);
    setErroLista('');
    setBloqueios({});
    try {
      const { data } = await api.get('/lotacao', {
        params: { gerencia_id: gerenciaId },
      });
      // resposta de um pedido que já foi superado: descarta
      if (ultimoPedido.current !== gerenciaId) return;
      // sem `ativo` na query: aqui a lista tem que vir completa
      const ordenadas = [...data].sort((a, b) => {
        const porEscala = a.escala.nome.localeCompare(b.escala.nome);
        return porEscala !== 0
          ? porEscala
          : a.ala.nome.localeCompare(b.ala.nome);
      });
      setLotacoes(ordenadas);
    } catch (e) {
      // GET não passa pelo toast do interceptor (api.js:70) — a tela avisa
      setErroLista('Não foi possível carregar as lotações desta gerência.');
      setLotacoes([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    carregarGerencias();
  }, []);

  useEffect(() => {
    if (gerencia) carregarLotacoes(gerencia.value);
  }, [gerencia]);

  const abrirModal = async () => {
    setEscalaId('');
    setAlaId('');
    setModoNovaAla(false);
    setNovaAla('');
    setModalAberto(true);

    const [respEscalas, respAlas] = await Promise.all([
      api.get('/escala'),
      api.get('/ala'),
    ]);
    setEscalas(respEscalas.data.filter(e => e.ativo));
    setAlas(respAlas.data.filter(a => a.ativo));
  };

  // Próxima posição livre abaixo do pai: se ele é 3.1 e já existem 3.1.1 até
  // 3.1.5, sai 3.1.6. O valor não é editável na tela — a estrutura vem das
  // escolhas de "fica abaixo de" + visibilidade, e os códigos aparecem só como
  // conferência no rodapé do modal.
  const proximaEstruturaAbaixoDe = pai => {
    if (!pai || !pai.formal) return '';
    const filhos = gerencias
      .map(g => g.formal)
      .filter(f => f && f.startsWith(`${pai.formal}.`))
      .map(f => Number(f.slice(pai.formal.length + 1).split('.')[0]))
      .filter(n => !Number.isNaN(n));
    const proximo = filhos.length > 0 ? Math.max(...filhos) + 1 : 1;
    return `${pai.formal}.${proximo}`;
  };

  // --- estrutura calculada a partir das escolhas visuais -------------------
  //
  // Ninguém digita código: escolhe a gerência acima e o que a nova enxerga.
  // A formal sai da posição livre abaixo do pai; a lógica sai da visibilidade.
  const estruturaFormal = proximaEstruturaAbaixoDe(gerenciaPai);
  const estruturaLogica =
    visibilidade === 'herdar' && gerenciaPai
      ? gerenciaPai.logica
      : estruturaFormal;

  // Mesma regra do backend (src/app/utils/escopoEstruturaLogica.js): uma
  // gerência A enxerga B quando é o mesmo código ou quando B está estritamente
  // abaixo — o ponto é o separador. Comparar prefixo cru diria que Angra
  // (3.1.1) enxerga 3.1.10, que é o irmão de número 10, não uma subordinada.
  const ehAncestral = (logicaDeQuemVe, logicaAlvo) =>
    logicaAlvo === logicaDeQuemVe ||
    logicaAlvo.startsWith(`${logicaDeQuemVe}.`);

  // o que a nova gerência vai enxergar
  const vaiEnxergar = estruturaLogica
    ? gerencias.filter(g => ehAncestral(estruturaLogica, g.logica))
    : [];

  // quem vai enxergar a nova
  const seraVistaPor = estruturaLogica
    ? gerencias.filter(g => ehAncestral(g.logica, estruturaLogica))
    : [];

  const abrirModalGerencia = () => {
    setNovaGerencia({ nome: '', sigla: '' });
    setGerenciaPai(gerencia);
    setVisibilidade('propria');
    setModalGerencia(true);
  };

  const salvarGerencia = async () => {
    setSalvando(true);
    try {
      const { data } = await api.post('/gerencia', {
        ...novaGerencia,
        estrutura_formal: estruturaFormal,
        estrutura_logica: estruturaLogica,
        // o backend deriva daqui o gerencia_pg_id (quem paga). Sem isso a
        // gerência nasce com o campo nulo e o select de VTR do BDT quebra.
        gerencia_pai_id: gerenciaPai ? gerenciaPai.value : null,
      });
      toast.success('Gerência criada!');
      setModalGerencia(false);
      await carregarGerencias();
      // já leva o usuário para a gerência recém-criada
      if (data && data.gerencia) {
        setGerencia({
          value: data.gerencia.id,
          label: data.gerencia.nome,
          logica: data.gerencia.estrutura_logica,
          formal: data.gerencia.estrutura_formal,
        });
      }
    } catch (e) {
      // modal fica aberto para corrigir; a mensagem vem do interceptor
    }
    setSalvando(false);
  };

  // Alterna ativo/inativo. Sem toast.error no catch: o interceptor
  // (services/api.js:77) já toasta toda falha de escrita — seria mensagem dupla.
  const alternarAtivo = async lotacao => {
    try {
      await api.put(`/lotacao/${lotacao.id}`, { ativo: !lotacao.ativo });
      toast.success(lotacao.ativo ? 'Lotação inativada' : 'Lotação reativada');
      await carregarLotacoes(gerencia.value);
    } catch (e) {
      const pessoas =
        e.response && e.response.data && e.response.data.pessoas_ativas;
      if (pessoas) setBloqueios(b => ({ ...b, [lotacao.id]: pessoas }));
    }
  };

  // Espelha alternarAtivo: sem toast.error no catch, porque o interceptor de
  // services/api.js já toasta falha de escrita. O 409 traz pessoas_ativas e o
  // aviso fica fixo na linha, que é onde a informação importa.
  const alternarAtivoGerencia = async g => {
    try {
      await api.put(`/gerencia/${g.value}`, { ativo: !g.ativo });
      toast.success(g.ativo ? 'Gerência inativada' : 'Gerência reativada');
      setBloqueiosGerencia(b => ({ ...b, [g.value]: undefined }));
      await carregarGerencias();
    } catch (e) {
      const pessoas =
        e.response && e.response.data && e.response.data.pessoas_ativas;
      if (pessoas) setBloqueiosGerencia(b => ({ ...b, [g.value]: pessoas }));
    }
  };

  // alas que esta gerência + escala ainda NÃO têm (o par ocupado continua
  // ocupado mesmo inativo — a inativa se resolve pelo botão Reativar da lista)
  const alasDisponiveis = () => {
    const usadas = lotacoes
      .filter(l => String(l.escala.id) === String(escalaId))
      .map(l => l.ala.id);
    return alas.filter(a => !usadas.includes(a.id));
  };

  // no modo "nova ala", avisa quando o nome já existe em vez de barrar
  const alaExistenteComONome = () =>
    alas.find(a => chaveNome(a.nome) === chaveNome(novaAla));

  const salvar = async () => {
    if (!escalaId) {
      toast.error('Selecione a Escala');
      return;
    }
    if (!modoNovaAla && !alaId) {
      toast.error('Selecione a Ala');
      return;
    }
    if (modoNovaAla && novaAla.trim().length < 3) {
      toast.error('O nome da ala precisa de ao menos 3 caracteres');
      return;
    }

    setSalvando(true);
    try {
      let idDaAla = alaId;

      if (modoNovaAla) {
        // reaproveita a ala existente em vez de tentar criar duplicada — o
        // AlaController recusa nome repetido, e criar não é o objetivo aqui
        const existente = alaExistenteComONome();
        if (existente) {
          idDaAla = existente.id;
        } else {
          const { data } = await api.post('/ala', { nome: novaAla.trim() });
          idDaAla = data.id;
        }
      }

      await api.post('/lotacao', {
        gerencia_id: gerencia.value,
        escala_id: escalaId,
        ala_id: idDaAla,
      });

      toast.success('Lotação vinculada!');
      setModalAberto(false);
      await carregarLotacoes(gerencia.value);
    } catch (e) {
      // modal continua aberto para corrigir a escolha; a mensagem (inclusive o
      // 409 de lotação já vinculada) vem do interceptor
    }
    setSalvando(false);
  };

  return (
    <NavBar>
      <MDBContainer>
        <MDBRow className="d-flex justify-content-between align-items-center mx-3">
          <MDBCol col="12" md="5" className="h4-responsive font-weight-bold">
            Gerência × Lotação
          </MDBCol>
          <MDBCol col="12" md="7" className="d-flex justify-content-end">
            <MDBBtn color="light" size="sm" onClick={abrirModalGerencia}>
              <MDBIcon icon="sitemap" className="mr-2" />
              Nova gerência
            </MDBBtn>
            <MDBBtn
              color="primary"
              size="sm"
              onClick={abrirModal}
              disabled={!gerencia}
            >
              <MDBIcon icon="plus" className="mr-2" />
              Vincular lotação
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        {erroListaGerencias ? (
          <MDBRow
            style={{ borderRadius: '5px' }}
            className="p-3 mr-1 ml-1 mt-1 mr-md-3 ml-md-3 bg-white justify-content-center align-items-center"
          >
            <span style={{ color: COR_DESTRUTIVA }}>{erroListaGerencias}</span>
            <MDBBtn
              size="sm"
              color="light"
              className="ml-3"
              onClick={carregarGerencias}
            >
              Tentar de novo
            </MDBBtn>
          </MDBRow>
        ) : (
          <TabelaGerencias
            gerencias={gerencias}
            selecionada={gerencia}
            onSelecionar={setGerencia}
            onAlternarAtivo={alternarAtivoGerencia}
            bloqueios={bloqueiosGerencia}
          />
        )}

        <MDBRow className="mt-4 mr-1 ml-1 mr-md-3 ml-md-3">
          <h6 className="grey-text mb-0">
            {gerencia
              ? `Lotações de ${gerencia.label}`
              : 'Selecione uma gerência acima'}
          </h6>
        </MDBRow>

        <MDBRow
          between
          style={{ borderRadius: '5px' }}
          className="p-2 mr-1 ml-1 mt-1 mr-md-3 ml-md-3 align-items-center grey darken-1 d-none d-md-flex"
        >
          <div className="col-md-3 font-weight-bold text-white">Gerência</div>
          <div className="col-md-2 font-weight-bold text-white">Escala</div>
          <div className="col-md-3 font-weight-bold text-white">Ala</div>
          <div className="col-md-2 font-weight-bold text-white">Pessoas</div>
          {/* sem coluna de status: o rótulo do botão já diz o estado da linha
              ("Inativar" só aparece no que está ativo), e em texto — não é
              informação transmitida apenas por cor. */}
          <div className="col-md-2 font-weight-bold text-white text-right" />
        </MDBRow>

        {loading ? (
          <Loading />
        ) : erroLista ? (
          <MDBRow
            style={{ borderRadius: '5px' }}
            className="p-3 mr-1 ml-1 mt-1 mr-md-3 ml-md-3 bg-white justify-content-center align-items-center"
          >
            <span style={{ color: COR_DESTRUTIVA }}>{erroLista}</span>
            <MDBBtn
              size="sm"
              color="light"
              className="ml-3"
              onClick={() => carregarLotacoes(gerencia.value)}
            >
              Tentar de novo
            </MDBBtn>
          </MDBRow>
        ) : (
          <TabelaLotacoes
            lotacoes={lotacoes}
            bloqueios={bloqueios}
            onAlternarAtivo={alternarAtivo}
            gerenciaSelecionada={gerencia}
          />
        )}

        <br />
        <br />

        <MDBModal
          isOpen={modalAberto}
          toggle={() => setModalAberto(false)}
          size="lg"
        >
          <MDBModalHeader toggle={() => setModalAberto(false)}>
            Vincular lotação
          </MDBModalHeader>

          <MDBModalBody>
            <p className="mb-3">
              <span className="grey-text">Gerência:</span>{' '}
              <span className="font-weight-bold">
                {gerencia && gerencia.label}
              </span>
            </p>

            <MDBRow>
              <MDBCol col="12" md="6">
                <label className="grey-text">
                  <Obrigatorio texto="Escala" />
                </label>
                <select
                  className="browser-default custom-select"
                  value={escalaId}
                  onChange={e => {
                    setEscalaId(e.target.value);
                    setAlaId('');
                    setModoNovaAla(false);
                  }}
                >
                  <option value="">Selecione...</option>
                  {escalas.map(es => (
                    <option key={es.id} value={String(es.id)}>
                      {es.nome}
                    </option>
                  ))}
                </select>
              </MDBCol>

              <MDBCol col="12" md="6">
                <div className="d-flex justify-content-between align-items-end">
                  <label className="grey-text mb-0">
                    <Obrigatorio
                      texto={modoNovaAla ? 'Nome da nova ala' : 'Ala'}
                    />
                  </label>
                  <span
                    style={{ cursor: 'pointer' }}
                    className="blue-grey-text small"
                    onClick={() => {
                      setModoNovaAla(!modoNovaAla);
                      setAlaId('');
                      setNovaAla('');
                    }}
                  >
                    {modoNovaAla ? (
                      <>
                        <MDBIcon icon="arrow-left" className="mr-1" />
                        voltar
                      </>
                    ) : (
                      <>
                        <MDBIcon icon="plus" className="mr-1" />
                        nova ala
                      </>
                    )}
                  </span>
                </div>

                {modoNovaAla ? (
                  <>
                    {/* ala.nome é VARCHAR(20) NOT NULL */}
                    <MDBInput
                      outline
                      maxLength={20}
                      value={novaAla}
                      onChange={e => setNovaAla(e.target.value)}
                    />
                    {alaExistenteComONome() && (
                      <p className="grey-text mb-0" style={{ fontSize: 12 }}>
                        {`Já existe a ala "${
                          alaExistenteComONome().nome
                        }" — ela será reaproveitada.`}
                      </p>
                    )}
                  </>
                ) : (
                  <select
                    className="browser-default custom-select"
                    value={alaId}
                    disabled={!escalaId}
                    onChange={e => setAlaId(e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {alasDisponiveis().map(a => (
                      <option key={a.id} value={String(a.id)}>
                        {a.nome}
                      </option>
                    ))}
                  </select>
                )}
              </MDBCol>
            </MDBRow>

            <LegendaObrigatorios />
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn
              color="light"
              size="sm"
              onClick={() => setModalAberto(false)}
            >
              Cancelar
            </MDBBtn>
            <MDBBtn
              color="primary"
              size="sm"
              disabled={salvando}
              onClick={salvar}
            >
              {salvando ? 'Salvando...' : 'Salvar'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>

        {/* ---- modal Nova gerência ---- */}
        <MDBModal
          isOpen={modalGerencia}
          toggle={() => setModalGerencia(false)}
          size="lg"
        >
          <MDBModalHeader toggle={() => setModalGerencia(false)}>
            Nova gerência
          </MDBModalHeader>

          <MDBModalBody>
            <MDBRow>
              <MDBCol col="12" md="8">
                {/* gerencia.nome é VARCHAR(50) NOT NULL */}
                <MDBInput
                  outline
                  label={<Obrigatorio texto="Nome" />}
                  maxLength={50}
                  value={novaGerencia.nome}
                  onChange={e =>
                    setNovaGerencia({ ...novaGerencia, nome: e.target.value })
                  }
                />
              </MDBCol>
              <MDBCol col="12" md="4">
                {/* gerencia.sigla é VARCHAR(15) NOT NULL */}
                <MDBInput
                  outline
                  label={<Obrigatorio texto="Sigla" />}
                  maxLength={15}
                  value={novaGerencia.sigla}
                  onChange={e =>
                    setNovaGerencia({ ...novaGerencia, sigla: e.target.value })
                  }
                />
              </MDBCol>
            </MDBRow>

            {/* Estrutura por escolha, não por digitação: os códigos são
                calculados e mostrados só para conferência no rodapé. */}
            <MDBRow className="mt-3">
              <MDBCol col="12" md="6">
                <label className="grey-text">
                  <Obrigatorio texto="Fica abaixo de" />
                </label>
                <select
                  className="browser-default custom-select"
                  value={gerenciaPai ? String(gerenciaPai.value) : ''}
                  onChange={e =>
                    setGerenciaPai(
                      gerencias.find(g => String(g.value) === e.target.value) ||
                        null
                    )
                  }
                >
                  <option value="">Selecione...</option>
                  {/* gerência inativa não serve de pai: a nova herdaria o
                      escopo e a pagadora de uma estrutura fora de uso */}
                  {gerencias
                    .filter(g => g.ativo)
                    .map(g => (
                      <option key={g.value} value={String(g.value)}>
                        {g.label}
                      </option>
                    ))}
                </select>
              </MDBCol>

              <MDBCol col="12" md="6">
                <label className="grey-text d-block">
                  O que esta gerência enxerga
                </label>
                {/* padrão Bootstrap custom-radio: o input cru fica invisível no
                    tema mdbreact (mesmo caso de Administracao/Perfis:449) */}
                <div className="custom-control custom-radio">
                  <input
                    type="radio"
                    className="custom-control-input"
                    id="visibilidadePropria"
                    name="visibilidadeGerencia"
                    checked={visibilidade === 'propria'}
                    onChange={() => setVisibilidade('propria')}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="visibilidadePropria"
                  >
                    Apenas ela mesma e suas subordinadas
                  </label>
                </div>
                <div className="custom-control custom-radio">
                  <input
                    type="radio"
                    className="custom-control-input"
                    id="visibilidadeHerdar"
                    name="visibilidadeGerencia"
                    disabled={!gerenciaPai}
                    checked={visibilidade === 'herdar'}
                    onChange={() => setVisibilidade('herdar')}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="visibilidadeHerdar"
                  >
                    {gerenciaPai
                      ? `Tudo que ${gerenciaPai.label} enxerga`
                      : 'Tudo que a gerência acima enxerga'}
                  </label>
                </div>
              </MDBCol>
            </MDBRow>

            {/* Prévia: o efeito da escolha, em nomes, antes de gravar */}
            {gerenciaPai && (
              <div className="mt-3 p-3" style={{ background: '#f5f5f5' }}>
                <p className="mb-1">
                  <b>Vai enxergar {vaiEnxergar.length + 1} gerência(s):</b>{' '}
                  {novaGerencia.nome || 'esta gerência'} (ela mesma)
                  {vaiEnxergar.length > 0 &&
                    `, ${vaiEnxergar.map(g => g.label).join(', ')}`}
                </p>
                <p className="mb-0">
                  <b>Será vista por:</b>{' '}
                  {seraVistaPor.length > 0
                    ? seraVistaPor.map(g => g.label).join(', ')
                    : 'ninguém além dela mesma'}
                </p>


                <p className="grey-text mb-0 mt-2" style={{ fontSize: 12 }}>
                  códigos gerados: formal <code>{estruturaFormal}</code> ·
                  lógica <code>{estruturaLogica}</code>
                </p>
              </div>
            )}

            <LegendaObrigatorios />
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn
              color="light"
              size="sm"
              onClick={() => setModalGerencia(false)}
            >
              Cancelar
            </MDBBtn>
            <MDBBtn
              color="primary"
              size="sm"
              disabled={salvando}
              onClick={salvarGerencia}
            >
              {salvando ? 'Salvando...' : 'Salvar'}
            </MDBBtn>
          </MDBModalFooter>
        </MDBModal>
      </MDBContainer>
    </NavBar>
  );
}
